import { useEffect, useMemo, useState } from "react";

const rarityTone = {
  common: "text-amber-200 border-amber-900/30 bg-amber-950/10",
  uncommon: "text-amber-200 border-amber-900/30 bg-amber-950/10",
  rare: "text-amber-200 border-amber-900/30 bg-amber-950/10",
  elite: "text-amber-200 border-amber-900/30 bg-amber-950/10",
  legendary: "text-amber-300 border-amber-900/30 bg-amber-950/10",
};

export const MiningDiscoveryPanel = ({ discoveries, pageSize = 6 }) => {
  const [page, setPage] = useState(1);
  const entries = discoveries?.entries || [];
  const totalPages = Math.max(1, Math.ceil(entries.length / pageSize));

  useEffect(() => {
    setPage((current) => Math.min(current, totalPages));
  }, [totalPages]);

  const paginatedEntries = useMemo(() => {
    const start = (page - 1) * pageSize;
    return entries.slice(start, start + pageSize);
  }, [entries, page, pageSize]);

  return (
    <div className="grid xl:grid-cols-[1.05fr_0.95fr] gap-6">
      <section data-testid="mining-discovery-journal" className="border border-amber-900/30 bg-zinc-950/80 p-4 rounded-sm">
        <div className="flex items-end justify-between gap-3">
          <div>
            <h2 className="font-cinzel text-base text-amber-300 uppercase tracking-wide">Discovery Journal</h2>
            <p className="text-xs text-zinc-500 mt-1">Rare seams and caches found while mining.</p>
          </div>
          <div data-testid="mining-discovery-total" className="text-xs text-zinc-400">{discoveries?.total_found || 0} logged</div>
        </div>

        <div className="mt-4 grid md:grid-cols-2 gap-3">
          {paginatedEntries.map((entry) => (
            <article key={entry.id} data-testid={`mining-discovery-entry-${entry.id}`} className="border border-zinc-800 bg-zinc-900/60 p-3 rounded-sm">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="font-cinzel text-sm text-zinc-100">{entry.name}</div>
                  <div className="text-xs uppercase tracking-[0.18em] text-zinc-500 mt-1">{entry.mine_names?.join(" • ")}</div>
                </div>
                <span className={`px-2 py-1 text-[11px] uppercase tracking-[0.18em] border ${rarityTone[entry.rarity] || "text-zinc-300 border-zinc-700 bg-zinc-900"}`}>
                  {entry.rarity}
                </span>
              </div>
              <div className="text-xs text-zinc-400 mt-3">{entry.summary}</div>
              <div className="mt-3 flex items-center justify-between gap-3 text-xs">
                <span className="text-zinc-500">Found</span>
                <span data-testid={`mining-discovery-count-${entry.id}`} className="font-cinzel text-zinc-100">{entry.count}</span>
              </div>
            </article>
          ))}
        </div>

        {entries.length > pageSize && (
          <div data-testid="mining-discovery-pagination" className="mt-4 flex items-center justify-between gap-3 border-t border-zinc-800 pt-3 text-xs text-zinc-500">
            <div>Showing {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, entries.length)} of {entries.length}</div>
            <div className="flex items-center gap-2">
              <button
                data-testid="mining-discovery-page-prev"
                type="button"
                onClick={() => setPage((current) => Math.max(1, current - 1))}
                disabled={page === 1}
                className="px-3 py-1.5 border border-zinc-800 text-zinc-300 hover:border-amber-700/40 hover:text-amber-300 rounded-sm disabled:opacity-50"
              >
                Previous
              </button>
              <span data-testid="mining-discovery-page-indicator" className="uppercase tracking-[0.18em]">Page {page} / {totalPages}</span>
              <button
                data-testid="mining-discovery-page-next"
                type="button"
                onClick={() => setPage((current) => Math.min(totalPages, current + 1))}
                disabled={page === totalPages}
                className="px-3 py-1.5 border border-zinc-800 text-zinc-300 hover:border-amber-700/40 hover:text-amber-300 rounded-sm disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </section>

      <section data-testid="mining-recent-discoveries" className="border border-amber-900/30 bg-zinc-950/80 p-4 rounded-sm">
        <h2 className="font-cinzel text-base text-amber-300 uppercase tracking-wide">Recent Finds</h2>
        <div className="mt-4 space-y-3">
          {discoveries?.recent?.length ? discoveries.recent.map((entry, index) => (
            <article key={`${entry.id}-${entry.discovered_at}-${index}`} data-testid={`mining-recent-discovery-${entry.id}-${index}`} className="border border-zinc-800 bg-zinc-900/60 p-3 rounded-sm">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="font-cinzel text-sm text-zinc-100">{entry.name}</div>
                  <div className="text-xs uppercase tracking-[0.18em] text-zinc-500 mt-1">{entry.mine_name}</div>
                </div>
                <div className="text-xs text-zinc-500">{new Date(entry.discovered_at).toLocaleString()}</div>
              </div>
                <div className="text-xs text-zinc-400 mt-2">{entry.summary}</div>
            </article>
          )) : (
            <div data-testid="mining-recent-discoveries-empty" className="border border-dashed border-amber-900/30 bg-zinc-950/40 p-6 text-center text-zinc-500">
              No discoveries logged yet.
            </div>
          )}
        </div>
      </section>
    </div>
  );
};